<template>
  <div class="media-preview">
    <button
      type="button"
      class="btn btn-header-start btn-success no-pseudo"
      @click="downloadMedia"
      :disabled="isDownloading"
      aria-label="Download"
    >
      <i class="fa-solid fa-download" v-if="!isDownloading" />
      <span class="spinner-border spinner-border-sm" v-else></span>
    </button>
    <button
      type="button"
      class="btn btn-header-end btn-light btn-hover-danger no-pseudo"
      @click="closePreview"
      aria-label="Close"
    >
      <i class="fa-solid fa-times" />
    </button>
    <div
      class="media-hint"
      v-if="showZoomTools"
      :class="{ 'media-hint-faded': !showHint }"
    >
      <i class="fa-solid fa-circle-info me-2"></i>
      Scroll to zoom · Click &amp; drag to pan
    </div>
    <div class="media-toolbar" v-if="showZoomTools">
      <button
        class="media-toolbar-btn"
        @click="zoomOut"
        :disabled="!osdReady"
        aria-label="Zoom out"
      >
        <i class="fa-solid fa-magnifying-glass-minus"></i>
      </button>
      <span class="media-toolbar-text">{{ zoomPercent }}%</span>
      <button
        class="media-toolbar-btn"
        @click="zoomIn"
        :disabled="!osdReady"
        aria-label="Zoom in"
      >
        <i class="fa-solid fa-magnifying-glass-plus"></i>
      </button>
      <span class="media-toolbar-divider"></span>
      <button
        class="media-toolbar-btn"
        @click="resetZoom"
        :disabled="!osdReady"
        aria-label="Fit to view"
      >
        <i class="fa-solid fa-expand"></i>
      </button>
    </div>
    <div class="media-container">
      <div
        class="media-content"
        style="
          height: calc(100% - 47px - 1rem);
          margin-top: calc(47px + 1rem);
          position: relative;
        "
      >
        <div class="placeholder" v-if="isLoading">
          <div class="spinner-border text-primary me-3"></div>
          <span class="text-muted">Loading media...</span>
        </div>
        <img
          :src="url"
          class="w-100 h-100"
          style="object-fit: contain"
          alt=""
          v-if="url.startsWith('http://semesta.beone.net.my/')"
        />
        <video
          :src="url"
          class="w-100 h-100"
          style="object-fit: contain"
          controls
          v-else-if="mimeType && mimeType.includes('video')"
        ></video>
        <div id="osd" v-else></div>
      </div>
    </div>
  </div>
</template>

<script>
import OSD from "openseadragon";

export default {
  name: "media-preview",
  props: ["url", "mimeType"],
  data() {
    return {
      osd: null,
      osdReady: false,
      isLoading: false,
      isDownloading: false,
      mediaBlob: null,
      zoomPercent: 100,
      showHint: true,
      hintTimer: null,
      hintEndAt: 0,
    };
  },
  computed: {
    showZoomTools() {
      return (
        !this.url.startsWith("http://semesta.beone.net.my/") &&
        !(this.mimeType && this.mimeType.includes("video"))
      );
    },
  },
  methods: {
    closePreview() {
      if (this.osd) {
        this.osd.removeAllHandlers("open");
      }

      this.$emit("close");
    },
    async prefetchBlob() {
      if (!this.url || this.url.startsWith("data:")) return;

      try {
        const res = await this.API.getExternal(this.url, "file");
        this.mediaBlob = res.data;
      } catch (err) {
        console.error("media prefetch failed", err);
      }
    },
    async downloadMedia() {
      if (this.isDownloading) return;

      this.isDownloading = true;

      try {
        if (this.mediaBlob) {
          const filename = this.Helper.deriveMediaFilename(this.url);
          const blobUrl = URL.createObjectURL(this.mediaBlob);
          const a = document.createElement("a");
          a.href = blobUrl;
          a.download = filename;
          document.body.appendChild(a);
          a.click();
          document.body.removeChild(a);
          URL.revokeObjectURL(blobUrl);
        } else {
          await this.Helper.downloadMedia(this.url);
        }
      } finally {
        this.isDownloading = false;
      }
    },
    loadOSD() {
      this.isLoading = true;

      if (this.osd) {
        this.osd.removeAllHandlers("open");
        this.osd.removeAllHandlers("zoom");
        this.osd.removeAllHandlers("pan");
      }

      this.osd = OSD({
        id: "osd",
        prefixUrl: "/osd/",
        // crossOriginPolicy: "Anonymous",
        maxZoomPixelRatio: 10,
        showNavigationControl: false,
        zoomPerClick: 1.0,
        constrainDuringPan: true,
        visibilityRatio: 1,
        animationTime: 0.3,
      }).open({
        type: "image",
        url: `${this.url}`,
      });

      this.osd.addHandler("open", () => {
        this.isLoading = false;
        this.osdReady = true;
        this.updateZoom();
      });

      this.osd.addHandler("zoom", () => {
        this.updateZoom();
        this.triggerHint(2000);
      });

      this.osd.addHandler("pan", () => {
        this.triggerHint(2000);
      });
    },
    updateZoom() {
      if (!this.osd || !this.osd.viewport) return;

      const current = this.osd.viewport.getZoom();
      const home = this.osd.viewport.getHomeZoom();
      if (!home) return;

      this.zoomPercent = Math.round((current / home) * 100);
    },
    triggerHint(duration) {
      const newEnd = Date.now() + duration;

      this.showHint = true;

      // Don't shorten an existing longer timer (e.g. the initial 3s shouldn't
      // get cut down to 2s by an OSD setup zoom event).
      if (this.hintTimer && this.hintEndAt > newEnd) return;

      if (this.hintTimer) clearTimeout(this.hintTimer);

      this.hintEndAt = newEnd;
      this.hintTimer = setTimeout(() => {
        this.showHint = false;
        this.hintTimer = null;
        this.hintEndAt = 0;
      }, duration);
    },
    zoomIn() {
      if (!this.osd) return;
      this.osd.viewport.zoomBy(1.5);
      this.osd.viewport.applyConstraints();
    },
    zoomOut() {
      if (!this.osd) return;
      this.osd.viewport.zoomBy(1 / 1.5);
      this.osd.viewport.applyConstraints();
    },
    resetZoom() {
      if (!this.osd) return;
      this.osd.viewport.goHome();
    },
  },
  mounted() {
    if (
      !this.url.startsWith("http://semesta.beone.net.my/") &&
      !this.mimeType?.includes("video")
    ) {
      this.loadOSD();
    }

    this.prefetchBlob();

    if (this.showZoomTools) {
      this.triggerHint(3000);
    }

    this.onKeydown = (e) => {
      if (e.key === "Escape") this.closePreview();
    };
    document.addEventListener("keydown", this.onKeydown);
  },
  beforeDestroy() {
    if (this.onKeydown) {
      document.removeEventListener("keydown", this.onKeydown);
    }
    if (this.hintTimer) {
      clearTimeout(this.hintTimer);
    }
    this.closePreview();
  },
};
</script>

<style scoped>
.media-toolbar {
  position: absolute;
  top: 1.5rem;
  left: 50%;
  transform: translateX(-50%);
  background: rgba(0, 0, 0, 0.75);
  padding: 0.4rem 0.75rem;
  border-radius: 999px;
  display: flex;
  align-items: center;
  gap: 0.25rem;
  color: white;
  z-index: 98;
  user-select: none;
  backdrop-filter: blur(4px);
}
.media-toolbar-btn {
  background: transparent;
  border: none;
  color: white;
  padding: 0.25rem 0.6rem;
  cursor: pointer;
  font-size: 0.9rem;
  transition: opacity 0.15s;
}
.media-toolbar-btn:hover:not(:disabled) {
  opacity: 0.7;
}
.media-toolbar-btn:disabled {
  opacity: 0.35;
  cursor: not-allowed;
}
.media-toolbar-text {
  min-width: 50px;
  text-align: center;
  font-size: 0.875rem;
}
.media-toolbar-divider {
  border-left: 1px solid rgba(255, 255, 255, 0.25);
  height: 20px;
  margin: 0 0.5rem;
}
.media-hint {
  position: absolute;
  top: 6.5rem;
  left: 50%;
  transform: translateX(-50%);
  background: rgba(0, 0, 0, 0.7);
  padding: 0.7rem 1.4rem;
  border-radius: 999px;
  color: rgba(255, 255, 255, 0.95);
  font-size: 1rem;
  z-index: 98;
  pointer-events: none;
  user-select: none;
  white-space: nowrap;
  opacity: 1;
  transition: opacity 0.6s ease-out;
  backdrop-filter: blur(4px);
}
.media-hint-faded {
  opacity: 0;
}
</style>